import { useCallback } from 'react';

import { useBraze } from '@order/Braze';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a utility function for logging a placed order to Braze.
 *
 * @see {@link https://www.braze.com/docs/developer_guide/platform_integration_guides/react_native/analytics/#logging-purchases Logging purchases | React Native}
 * @see {@link https://www.braze.com/docs/developer_guide/platform_integration_guides/web/analytics/logging_purchases/ Logging purchases | Web}
 */
export const useCheckoutBrazeLogPurchase = (
  params: UseCheckoutBrazeLogPurchaseParams,
) => {
  const { orderDetails, orderLedger } = params;
  const { cart, lineItemsCount, cartOrderType } = orderDetails;

  // ─────────────────────────────────────────────

  const {
    subtotal = 0,
    discountsTotal = 0,
    creditsTotal = 0,
  } = orderLedger ?? {};
  const { restaurant, deliveryOrderDetail } = cart ?? {};
  const { deliveryFee = 0 } = deliveryOrderDetail ?? {};

  // ─── Discounts ───────────────────────────────

  const discountPlusCredits = discountsTotal + creditsTotal;
  const discountPlusCreditsFormatted = formatAmountInDollars(
    discountsTotal + creditsTotal,
  );

  // ─── Subtotal ────────────────────────────────

  const subtotalPlusDeliveryFee = subtotal + deliveryFee;
  const subtotalPlusDeliveryFeeFormatted = formatAmountInDollars(
    subtotalPlusDeliveryFee,
  );
  const subtotalNet = subtotalPlusDeliveryFee - discountPlusCredits;
  const subtotalNetFormatted = formatAmountInDollars(subtotalNet);
  const subtotalNetFormattedWithFallback = Math.max(subtotalNetFormatted, 0);

  // ─── Other details ───────────────────────────

  const currency = 'usd';
  const quantity = 1;
  const restaurantId = restaurant?.id ?? '';

  // ─────────────────────────────────────────────

  const { logPurchase } = useBraze();

  return useCallback(() => {
    const otherDetails = {
      discount: discountPlusCreditsFormatted,
      order_net: subtotalNetFormattedWithFallback,
      item_count: lineItemsCount,
      restaurant_id: restaurantId,
    };

    logPurchase?.(
      cartOrderType,
      subtotalPlusDeliveryFeeFormatted,
      currency,
      quantity,
      otherDetails,
    );
  }, [
    cartOrderType,
    discountPlusCreditsFormatted,
    lineItemsCount,
    logPurchase,
    restaurantId,
    subtotalNetFormattedWithFallback,
    subtotalPlusDeliveryFeeFormatted,
  ]);
};

// ─── Utils ───────────────────────────────────────────────────────────────────

function formatAmountInDollars(amount: number) {
  return amount / 100;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseCheckoutBrazeLogPurchaseParams = {
  readonly orderDetails: {
    readonly cart?: {
      readonly restaurant?: { readonly id: string } | null;
      readonly deliveryOrderDetail?: { readonly deliveryFee: number } | null;
    } | null;
    readonly lineItemsCount: number;
    readonly cartOrderType: string;
  };
  readonly orderLedger?: {
    readonly subtotal?: number;
    readonly discountsTotal?: number;
    readonly creditsTotal?: number;
  };
};
